import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "setup-provider-modal" }
const _hoisted_2 = { class: "setup-provider-modal__logos" }
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = { class: "setup-provider-modal__arrow" }
const _hoisted_5 = { class: "setup-provider-modal__subtitle typographies-paragraph-medium-regular" }
const _hoisted_6 = { class: "setup-provider-modal__info-cards" }
const _hoisted_7 = { class: "setup-provider-modal__fees-content" }
const _hoisted_8 = { class: "setup-provider-modal__info-card-value typographies-paragraph-x-large-regular" }
const _hoisted_9 = { class: "setup-provider-modal__payout-text" }
const _hoisted_10 = { class: "setup-provider-modal__info-card-value typographies-paragraph-x-large-regular" }
const _hoisted_11 = { class: "setup-provider-modal__info-card-footnote typographies-paragraph-small-regular" }

import { computed, inject } from 'vue';
import { Translator } from 'js/shims-vue';
import QuickpaySettingsFaq from 'vcomponents/quickpay/settings/QuickpaySettingsFaq.vue';
import QuickpaySettingsSetupProviderInfoCard from 'vcomponents/quickpay/settings/QuickpaySettingsSetupProviderInfoCard.vue';

interface PaymentMethod {
    icon: string;
    labelKey: string;
}

interface FaqItem {
    questionKey: string;
    answerKey: string;
}

interface ProviderSetupModal {
    subtitleKey: string;
    paymentMethods: PaymentMethod[];
    fees: { amountKey: string; pricingUrl?: string };
    payout: { delayKey: string; subtitleKey: string };
    advantages: string[];
    learnMoreUrl?: string;
    faq?: FaqItem[];
}

interface Provider {
    id: string;
    name: string;
    logo: string;
    setupModal: ProviderSetupModal;
}

interface Props {
    provider: Provider;
    isLoading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QuickpaySettingsSetupProviderModal',
  props: /*@__PURE__*/_mergeModels({
    provider: {},
    isLoading: { type: Boolean, default: false }
  }, {
    "visible": { type: Boolean, default: false },
    "visibleModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(['connectProvider'], ["update:visible"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isVisible = _useModel(__props, 'visible');

const $t = inject('$t') as Translator;

const modalTitle = computed((): string =>
    $t.__('quickpay.settings.setupModal.title', { providerName: props.provider.name }),
);

const primaryAction = computed((): { label: string; loading: boolean; action: () => void } => ({
    label: $t.__('quickpay.settings.setupModal.common.connectAccount', {
        providerName: props.provider.name,
    }),
    loading: props.isLoading,
    action: () => emit('connectProvider'),
}));

const secondaryActions = computed((): { label: string; leftIcon: string; action: () => void }[] => {
    if (!props.provider.setupModal.learnMoreUrl) {
        return [];
    }
    return [
        {
            label: $t.__('common.labels.learn_more'),
            leftIcon: 'external-link',
            action: () => {
                window.open(
                    props.provider.setupModal.learnMoreUrl,
                    '_blank',
                    'noopener,noreferrer',
                );
            },
        },
    ];
});

const advantagesTitle = computed((): string =>
    $t.__('quickpay.settings.setupModal.common.advantagesTitle', {
        providerName: props.provider.name,
    }),
);

const advantageItems = computed((): string[] =>
    props.provider.setupModal.advantages.map((key) => $t.__(key)),
);

const subtitle = computed((): string =>
    $t.__(props.provider.setupModal.subtitleKey),
);

const paymentMethods = computed((): { icon: string; label: string }[] =>
    props.provider.setupModal.paymentMethods.map((method) => ({
        icon: method.icon,
        label: $t.__(method.labelKey),
    })),
);

const feesAmount = computed((): string =>
    $t.__(props.provider.setupModal.fees.amountKey),
);

const payoutDelay = computed((): string =>
    $t.__(props.provider.setupModal.payout.delayKey),
);

const payoutSubtitle = computed((): string =>
    $t.__(props.provider.setupModal.payout.subtitleKey),
);

const faqItems = computed((): FaqItem[] => props.provider.setupModal.faq ?? []);

const translations = {
    paymentMethodsLabel: $t.__('common.labels.payment_method'),
    feesLabel: $t.__('common.labels.fees'),
    payoutLabel: $t.__('common.labels.payout'),
    pricingLink: $t.__('quickpay.settings.setupModal.common.pricingLink'),
};

const openPricingUrl = (): void => {
    window.open(props.provider.setupModal.fees.pricingUrl, '_blank', 'noopener,noreferrer');
};

return (_ctx: any,_cache: any) => {
  const _component_CompassIcon = _resolveComponent("CompassIcon")!
  const _component_CompassPill = _resolveComponent("CompassPill")!
  const _component_CompassPlainButton = _resolveComponent("CompassPlainButton")!
  const _component_CompassCallout = _resolveComponent("CompassCallout")!
  const _component_CompassModal = _resolveComponent("CompassModal")!

  return (_openBlock(), _createBlock(_component_CompassModal, {
    visible: isVisible.value,
    "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((isVisible).value = $event)),
    title: modalTitle.value,
    primaryAction: primaryAction.value,
    secondaryActions: secondaryActions.value,
    closeOnClickOverlay: "",
    onClosed: _cache[1] || (_cache[1] = ($event: any) => (isVisible.value = false))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("img", {
            src: __props.provider.logo,
            alt: __props.provider.name,
            class: "setup-provider-modal__logo"
          }, null, 8 /* PROPS */, _hoisted_3),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_CompassIcon, {
              icon: "arrow-right",
              size: _ctx.CompassEnums.IconSize.SMALL
            }, null, 8 /* PROPS */, ["size"]),
            _createVNode(_component_CompassIcon, {
              icon: "arrow-left",
              size: _ctx.CompassEnums.IconSize.SMALL
            }, null, 8 /* PROPS */, ["size"])
          ]),
          _cache[2] || (_cache[2] = _createElementVNode("img", {
            src: "/ressources/logged/img/icon/svg/sellsyCloud.svg",
            alt: "Sellsy",
            class: "setup-provider-modal__logo"
          }, null, -1 /* CACHED */))
        ]),
        _createElementVNode("p", _hoisted_5, _toDisplayString(subtitle.value), 1 /* TEXT */),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(QuickpaySettingsSetupProviderInfoCard, {
            icon: "subscription",
            title: translations.paymentMethodsLabel
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paymentMethods.value, (method, index) => {
                return (_openBlock(), _createBlock(_component_CompassPill, {
                  key: index,
                  icon: method.icon,
                  label: method.label,
                  variant: _ctx.CompassEnums.PillVariant.PASTEL,
                  color: _ctx.CompassEnums.PillColor.PURPLE,
                  size: _ctx.CompassEnums.PillSize.SMALL
                }, null, 8 /* PROPS */, ["icon", "label", "variant", "color", "size"]))
              }), 128 /* KEYED_FRAGMENT */))
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title"]),
          _createVNode(QuickpaySettingsSetupProviderInfoCard, {
            icon: "moneybag-move-back",
            title: translations.feesLabel
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString(feesAmount.value), 1 /* TEXT */),
                (__props.provider.setupModal.fees.pricingUrl)
                  ? (_openBlock(), _createBlock(_component_CompassPlainButton, {
                      key: 0,
                      label: translations.pricingLink,
                      size: _ctx.CompassEnums.PlainButtonSize.SMALL,
                      variant: _ctx.CompassEnums.PlainButtonVariantType.NEUTRAL,
                      leftIcon: "external-link",
                      onClick: openPricingUrl
                    }, null, 8 /* PROPS */, ["label", "size", "variant"]))
                  : _createCommentVNode("v-if", true)
              ])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title"]),
          _createVNode(QuickpaySettingsSetupProviderInfoCard, {
            icon: "clock-up",
            title: translations.payoutLabel
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("span", _hoisted_10, _toDisplayString(payoutDelay.value), 1 /* TEXT */),
                _createElementVNode("span", _hoisted_11, _toDisplayString(payoutSubtitle.value), 1 /* TEXT */)
              ])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title"])
        ]),
        _createVNode(_component_CompassCallout, {
          title: advantagesTitle.value,
          icon: "success-circle-fill",
          items: advantageItems.value
        }, null, 8 /* PROPS */, ["title", "items"]),
        (faqItems.value.length)
          ? (_openBlock(), _createBlock(QuickpaySettingsFaq, {
              key: 0,
              items: faqItems.value
            }, null, 8 /* PROPS */, ["items"]))
          : _createCommentVNode("v-if", true)
      ])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["visible", "title", "primaryAction", "secondaryActions"]))
}
}

})